import { EntityType, getCookie } from '@frontend/common';
import { FunctionalityClient } from '@frontend/functionality/api';
import { Application, Feature, FeatureTarget, Functionality } from '@frontend/functionality/types';
import { CommonMessage } from '@frontend/lang';
import { SettingClient } from '@frontend/setting/api';
import { SettingValue } from '@frontend/setting/types';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

import { useAppSelector } from '../redux/store';

const LicenseStoreContext = createContext<{
    isLoading: boolean;
    functionalities: Functionality[];
    kioskFunctionalities: Functionality[];
    userFunctionalities: Functionality[];
    allFunctionalities: Functionality[];
    getFunctionality: (application: Application, feature: Feature) => Functionality | undefined;
    getFunctionalitySetting: (functionalityId: string) => { [k: string]: SettingValue | undefined };
    settings: FunctionalitySettings;
} | null>(null);

type FunctionalitySettings = {
    [key: string]: {
        [k: string]: SettingValue;
    };
};
const TELLOPORT_SELECTED_ACCOUNT_COOKIE = 'tp_prev_sel_acc';
export const LicenseStoreProvider = (props: { children: React.ReactNode }) => {
    const navigationState = useAppSelector((store) => store.navigation);
    const account_id = getCookie(TELLOPORT_SELECTED_ACCOUNT_COOKIE) ?? null;
    const [initialLoad, setInitialLoad] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [baseFunctionalities, setBaseFunctionalities] = useState<Functionality[] | null>(null);
    const [userFunctionalities, setUserFunctionalities] = useState<Functionality[] | null>(null);
    const [settings, setSettings] = useState<FunctionalitySettings>({});

    const functionalities = React.useMemo(() => {
        if (navigationState.user?.auth && userFunctionalities !== null) {
            return userFunctionalities;
        }
        return baseFunctionalities ?? [];
    }, [baseFunctionalities, navigationState.user?.auth, userFunctionalities]);

    const allFunctionalities = React.useMemo(() => {
        const byId = new Map<string, Functionality>();
        for (const functionality of baseFunctionalities ?? []) {
            byId.set(functionality.id, functionality);
        }
        for (const functionality of userFunctionalities ?? []) {
            byId.set(functionality.id, functionality);
        }
        return [...byId.values()];
    }, [baseFunctionalities, userFunctionalities]);

    //Handle initial load
    useEffect(() => {
        if (!account_id) return;
        setIsLoading(true);
        FunctionalityClient.fetchFunctionalities({
            account_id,
            sort_direction: 'asc',
            sort_field: 'order',
            target: FeatureTarget.KIOSK
        })
            .then((res) => setBaseFunctionalities(res.results))
            .catch(() => setBaseFunctionalities([]))
            .finally(() => {
                setIsLoading(false);
                setInitialLoad(false);
            });
    }, [account_id]);

    //TODO: handle multi user auth
    //Handle user auth change
    useEffect(() => {
        const auth = navigationState.user?.auth ?? null;
        if (auth === null) {
            setUserFunctionalities(null);
            return;
        }
        setIsLoading(true);
        FunctionalityClient.fetchFunctionalities(
            {
                sort_direction: 'asc',
                sort_field: 'order',
                account_id,
                target: FeatureTarget.KIOSK
            },
            auth,
            true
        )
            .then((res) => setUserFunctionalities(res.results))
            // Keep base list when the user-scoped list fails (e.g. 403).
            .catch(() => setUserFunctionalities(null))
            .finally(() => setIsLoading(false));
    }, [account_id, navigationState.user?.auth]);

    const settingsRef = useRef(settings);
    settingsRef.current = settings;

    useEffect(() => {
        if (allFunctionalities.length === 0) return;

        const missing = allFunctionalities.filter((f) => !settingsRef.current[f.id]);
        if (missing.length === 0) return;

        let cancelled = false;
        Promise.all(
            missing.map(async (f) => {
                const response = await SettingClient.fetchSettings({ entity_type: EntityType.FUNCTIONALITY, entity_id: f.id });
                return { functionality: f, settings: response };
            })
        )
            .then((res) => {
                if (cancelled) return;
                setSettings((current) => {
                    const next = { ...current };
                    res.forEach(({ functionality, settings: functionalitySettings }) => {
                        next[functionality.id] = Object.fromEntries(functionalitySettings.results.map((s) => [s.key, s.value]));
                    });
                    return next;
                });
            })
            .catch((err) => {
                console.error('Error fetching settings for functionalities', err);
            });

        return () => {
            cancelled = true;
        };
    }, [allFunctionalities]);

    const getFunctionalitySetting = (functionalityId: string) => {
        return settings[functionalityId] || {};
    };
    const getFunctionality = (application: Application, feature: Feature) => {
        return allFunctionalities.find((f) => f.application === application && f.feature === feature);
    };

    if (initialLoad) return <LoadingScreen />;
    return (
        <LicenseStoreContext.Provider
            value={{
                isLoading,
                functionalities,
                kioskFunctionalities: baseFunctionalities ?? [],
                userFunctionalities: userFunctionalities ?? [],
                allFunctionalities,
                getFunctionalitySetting,
                getFunctionality,
                settings
            }}>
            {props.children}
        </LicenseStoreContext.Provider>
    );
};

export const useLicenseRepository = () => {
    const context = useContext(LicenseStoreContext);
    if (!context) {
        throw new Error('useLicenseRepository must be used within a LicenseStoreProvider');
    }
    return context;
};

const LoadingScreen = () => {
    return (
        <div className='bg-always-black flex min-h-screen min-w-screen items-center justify-center'>
            <div className='flex flex-col items-center gap-8'>
                <div role='status'>
                    <svg
                        aria-hidden='true'
                        className={'fill-brand-primary text-brand-light relative size-16 animate-spin'}
                        viewBox='0 0 100 101'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'>
                        <path
                            d='M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z'
                            fill='currentColor'
                        />
                        <path
                            d='M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z'
                            fill='currentFill'
                        />
                    </svg>
                    <span className='sr-only'>{CommonMessage.FORMS.LOADING_PLACEHOLDER}</span>
                </div>
            </div>
        </div>
    );
};
